import React, { useState, useEffect } from 'react';
import { useGetLanlordTenantStaQuery } from '../../lib/apiSlice/communication/communicationSlice.tsx';

export default function ComposeCommunicationRecipients({ onRecipientsChange }) {
  // State for selected recipient type
  const [selectedRecipientType, setSelectedRecipientType] = useState("all");
  const { data: MetricStats, isLoading, error } = useGetLanlordTenantStaQuery();
  
  // State for search terms
  const [propertySearch, setPropertySearch] = useState("");
  const [tenantSearch, setTenantSearch] = useState("");

  // State for selected items
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedTenants, setSelectedTenants] = useState([]);

  // Format phone numbers to 254 format
  const formatPhoneNumber = (phone) => {
    if (phone.startsWith('0')) {
      return `254${phone.substring(1)}`;
    }
    return phone;
  };

  // Create unique tenant identifier
  const getTenantId = (tenant) => `${tenant.name}-${tenant.phone_number}`;

  // Filter functions using actual API data
  const filteredProperties = MetricStats?.apartments?.filter(apartment =>
    apartment.apartment_name.toLowerCase().includes(propertySearch.toLowerCase())
  ) || [];

  const filteredTenants = selectedRecipientType === "property" 
    ? (selectedProperties.flatMap(apartmentId => {
        const apartment = MetricStats?.apartments?.find(a => a.apartment_id === apartmentId);
        return apartment?.tenants || [];
      }).filter(tenant =>
        tenant?.name?.toLowerCase().includes(tenantSearch.toLowerCase())
      )) 
    : (MetricStats?.total_tenants?.filter(tenant =>
        tenant?.name?.toLowerCase().includes(tenantSearch.toLowerCase())
      ) || []);

  // Handler functions
  const handlePropertySelect = (apartmentId) => {
    setSelectedProperties(prev =>
      prev.includes(apartmentId)
        ? prev.filter(id => id !== apartmentId)
        : [...prev, apartmentId]
    );
  };

  const handleTenantSelect = (tenant) => {
    const tenantId = getTenantId(tenant);
    setSelectedTenants(prev =>
      prev.some(t => getTenantId(t) === tenantId)
        ? prev.filter(t => getTenantId(t) !== tenantId)
        : [...prev, tenant]
    );
  };

  const removeProperty = (apartmentId) => {
    setSelectedProperties(prev => prev.filter(id => id !== apartmentId));
  };

  const removeTenant = (tenantId) => {
    setSelectedTenants(prev => prev.filter(t => getTenantId(t) !== tenantId));
  };

  // Effect to notify parent about recipient changes
  useEffect(() => {
    if (!MetricStats || !onRecipientsChange) return;

    let recipientInfo = {
      type: selectedRecipientType,
      count: 0,
      recipients: []
    };

    switch (selectedRecipientType) {
      case "all":
        recipientInfo.count = MetricStats.total_tenants_count || 0;
        recipientInfo.recipients = MetricStats.total_tenants || [];
        break;
      case "property":
        const propertyTenants = selectedProperties.flatMap(apartmentId => {
          const apartment = MetricStats.apartments?.find(a => a.apartment_id === apartmentId);
          return apartment?.tenants || [];
        });
        recipientInfo.count = propertyTenants.length;
        recipientInfo.recipients = propertyTenants;
        recipientInfo.selectedProperties = selectedProperties.map(id => {
          const apt = MetricStats.apartments?.find(a => a.apartment_id === id);
          return { id, name: apt?.apartment_name };
        });
        break;
      case "individual":
        recipientInfo.count = selectedTenants.length;
        recipientInfo.recipients = selectedTenants;
        break;
      default:
        break;
    }

    onRecipientsChange(recipientInfo);
  }, [selectedRecipientType, selectedProperties, selectedTenants, MetricStats, onRecipientsChange]);

  if (isLoading) return <div className="p-4 text-center">Loading recipients...</div>;
  if (error) return <div className="p-4 text-red-500 text-center">Error loading recipients</div>;

  return (
    <div className="mb-6">
      <p className="block mb-2 font-medium text-gray-700 text-sm">Recipients</p>
      <div className="gap-3 grid grid-cols-3 mb-3">
        <button
          className={`py-2 px-4 rounded-md ${selectedRecipientType === "all"
            ? "bg-blue-500 text-white"
            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          onClick={() => {
            setSelectedRecipientType("all");
            setSelectedProperties([]);
            setSelectedTenants([]);
          }}
        >
          All Tenants ({MetricStats?.total_tenants_count || 0})
        </button>
        <button
          className={`py-2 px-4 rounded-md ${selectedRecipientType === "property"
            ? "bg-blue-500 text-white"
            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          onClick={() => {
            setSelectedRecipientType("property");
            setSelectedTenants([]);
          }}
        >
          By Property
        </button>
        <button
          className={`py-2 px-4 rounded-md ${selectedRecipientType === "individual"
            ? "bg-blue-500 text-white"
            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          onClick={() => {
            setSelectedRecipientType("individual");
            setSelectedProperties([]);
          }}
        >
          Individual Tenants
        </button>
      </div>

      <div className="p-3 border border-gray-300 rounded-md min-h-10">
        {selectedRecipientType === "all" && (
          <div className="inline-flex items-center bg-blue-100 px-2 py-1 rounded-md text-blue-800 text-sm">
            All Tenants ({MetricStats?.total_tenants_count || 0})
            <button className="ml-1 text-blue-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {selectedRecipientType === "property" && (
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Search properties..."
              className="mb-2 px-3 py-2 border border-gray-300 focus:border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              value={propertySearch}
              onChange={(e) => setPropertySearch(e.target.value)}
            />
            
            {/* Property selection list */}
            <div className="mb-2 max-h-40 overflow-y-auto">
              {filteredProperties.map(apartment => (
                <div 
                  key={apartment.apartment_id}
                  className={`p-2 mb-1 rounded-md cursor-pointer ${selectedProperties.includes(apartment.apartment_id) 
                    ? 'bg-blue-50 border border-blue-200' 
                    : 'hover:bg-gray-50'}`}
                  onClick={(e) => {
                    // Prevent double trigger from checkbox click
                    if (e.target.tagName !== 'INPUT') {
                      handlePropertySelect(apartment.apartment_id);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedProperties.includes(apartment.apartment_id)}
                    onChange={() => handlePropertySelect(apartment.apartment_id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mr-2"
                  />
                  {apartment.apartment_name} ({apartment.tenant_count} tenants)
                </div>
              ))}
            </div>

            {/* Selected properties */}
            <div className="flex flex-wrap gap-2">
              {selectedProperties.map(apartmentId => {
                const apartment = MetricStats?.apartments?.find(a => a.apartment_id === apartmentId);
                return (
                  <div key={apartmentId} className="inline-flex items-center bg-blue-100 px-2 py-1 rounded-md text-blue-800 text-sm">
                    {apartment?.apartment_name} ({apartment?.tenant_count})
                    <button 
                      className="ml-1 text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeProperty(apartmentId);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {selectedRecipientType === "individual" && (
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Search tenants..."
              className="mb-2 px-3 py-2 border border-gray-300 focus:border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
            />
            
            {/* Tenant selection list */}
            <div className="mb-2 max-h-40 overflow-y-auto">
              {filteredTenants.map(tenant => {
                const tenantId = getTenantId(tenant);
                const isSelected = selectedTenants.some(t => getTenantId(t) === tenantId);
                
                return (
                  <div 
                    key={tenantId}
                    className={`p-2 mb-1 rounded-md cursor-pointer ${isSelected
                      ? 'bg-blue-50 border border-blue-200' 
                      : 'hover:bg-gray-50'}`}
                    onClick={(e) => {
                      // Prevent double trigger from checkbox click
                      if (e.target.tagName !== 'INPUT') {
                        handleTenantSelect(tenant);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTenantSelect(tenant)}
                      onClick={(e) => e.stopPropagation()}
                      className="mr-2"
                    />
                    {tenant.name} ({formatPhoneNumber(tenant.phone_number)})
                  </div>
                );
              })}
            </div>

            {/* Selected tenants */}
            <div className="flex flex-wrap gap-2">
              {selectedTenants.map(tenant => {
                const tenantId = getTenantId(tenant);
                return (
                  <div key={tenantId} className="inline-flex items-center bg-blue-100 px-2 py-1 rounded-md text-blue-800 text-sm">
                    {tenant.name}
                    <button 
                      className="ml-1 text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTenant(tenantId);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}